import { useState } from "react";
import { Maximize2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { PromptEnhanceButton } from "@/components/ai/PromptEnhanceButton";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { Agent } from "@/types/agent/agent";

interface SystemPromptCardProps {
  agent: Agent;
  brand?: string;
}

export const SystemPromptCard = ({ agent, brand = "#4E9CFF" }: SystemPromptCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [prompt, setPrompt] = useState(
    agent.settings?.conversation_config?.agent?.prompt?.prompt ?? "",
  );
  const [expandedOpen, setExpandedOpen] = useState(false);
  const [tempPrompt, setTempPrompt] = useState("");

  const updateAgent = useUpdateAgent();

  const currentPrompt = agent.settings?.conversation_config?.agent?.prompt?.prompt ?? "";

  const handleEdit = () => {
    setPrompt(currentPrompt);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    await updateAgent.mutateAsync({
      agent,
      updateData: {
        settings: {
          ...agent.settings,
          conversation_config: {
            ...agent.settings?.conversation_config,
            agent: {
              ...agent.settings?.conversation_config?.agent,
              prompt: {
                ...agent.settings?.conversation_config?.agent?.prompt,
                prompt,
              },
            },
          },
        },
      },
    });
    toast.success("System prompt saved");
    setIsEditing(false);
  };

  const openExpanded = () => {
    setTempPrompt(prompt);
    setExpandedOpen(true);
  };

  return (
    <>
      <OverviewSectionCard
        title="System Prompt"
        subtitle="Define personality and behavior"
        genieIcon="scroll"
        brand={brand}
        secondaryColor={agent.branding?.secondaryColor}
        gradientEnabled={agent.branding?.gradientEnabled}
        gradientAngle={agent.branding?.gradientAngle}
        onEdit={isEditing ? undefined : handleEdit}
        onSave={isEditing ? handleSave : undefined}
        onCancel={isEditing ? handleCancel : undefined}
        isSaving={updateAgent.isPending}
      >
        {isEditing ? (
          <div className="space-y-4">
            <div className="border border-gray-300 rounded-md">
              <Tabs defaultValue="edit" className="w-full">
                <div className="flex items-center justify-between border-b border-gray-200">
                  <TabsList className="grid grid-cols-2 flex-1 bg-transparent border-none">
                    <TabsTrigger value="edit">Edit</TabsTrigger>
                    <TabsTrigger value="preview">Preview</TabsTrigger>
                  </TabsList>
                  <div className="flex items-center gap-1 pr-2">
                    <PromptEnhanceButton
                      currentPrompt={prompt}
                      onPromptEnhanced={setPrompt}
                      agentName={agent.name}
                      agentDescription={agent.description}
                      size="sm"
                      label="Enhance"
                    />
                    <button
                      type="button"
                      onClick={openExpanded}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                      title="Expand editor"
                    >
                      <Maximize2 className="h-4 w-4 text-gray-600" />
                    </button>
                  </div>
                </div>
                <TabsContent value="edit" className="mt-0">
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="w-full border-0 px-3 py-2 min-h-[300px] resize-y focus:outline-none focus:ring-0 text-sm"
                    placeholder="System prompt for the agent... (Markdown supported)"
                  />
                </TabsContent>
                <TabsContent value="preview" className="mt-0">
                  <div className="px-3 py-2 min-h-[300px] prose prose-sm max-w-none text-sm">
                    {prompt ? (
                      <ReactMarkdown>{prompt}</ReactMarkdown>
                    ) : (
                      <p className="text-gray-400 italic">System prompt preview will appear here...</p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

          </div>
        ) : (
          <div>
            <p className="text-sm text-gray-600 mb-3">
              This defines how your genie behaves, responds, and what knowledge it has
            </p>
            {currentPrompt ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-[600px] overflow-y-auto">
                <div className="prose prose-sm max-w-none text-sm text-gray-900 leading-relaxed">
                  <ReactMarkdown>{currentPrompt}</ReactMarkdown>
                </div>
              </div>
            ) : (
              <p className="text-gray-400 text-sm italic">No system prompt configured</p>
            )}
          </div>
        )}
      </OverviewSectionCard>

      <Dialog open={expandedOpen} onOpenChange={setExpandedOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit System Prompt</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            <Tabs defaultValue="edit" className="w-full h-full flex flex-col">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="edit">Edit</TabsTrigger>
                <TabsTrigger value="preview">Preview</TabsTrigger>
              </TabsList>
              <TabsContent value="edit" className="flex-1 mt-4 min-h-[500px]">
                <textarea
                  value={tempPrompt}
                  onChange={(e) => setTempPrompt(e.target.value)}
                  className="w-full h-full rounded-md border border-gray-300 px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[500px] text-sm"
                  placeholder="System prompt for the agent... (Markdown supported)"
                />
              </TabsContent>
              <TabsContent value="preview" className="flex-1 mt-4 min-h-[500px]">
                <div className="w-full h-full overflow-auto p-4 border border-gray-300 rounded-md bg-gray-50 min-h-[500px]">
                  <div className="prose prose-sm max-w-none">
                    {tempPrompt ? (
                      <ReactMarkdown>{tempPrompt}</ReactMarkdown>
                    ) : (
                      <p className="text-gray-400 italic">System prompt preview will appear here...</p>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setExpandedOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                setPrompt(tempPrompt);
                setExpandedOpen(false);
              }}
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
